import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Megaphone, HeartPulse, BookOpen, Plus, CheckCircle2, ShieldCheck, ScrollText, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CompliancePlusPage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("action") === "new-monitor" ? "frameworks" : "incidents";
  return (
    <div className="space-y-6" data-testid="compliance-plus-page">
      <div>
        <h1 className="text-3xl font-bold font-display">Compliance workspace</h1>
        <p className="text-sm text-neutral-500 mt-1">Health &amp; safety, framework builder, cert queue, and audit log.</p>
      </div>
      <Tabs defaultValue={initialTab}>
        <TabsList className="bg-neutral-100 flex-wrap h-auto">
          <TabsTrigger value="incidents" data-testid="cp-tab-incidents"><AlertTriangle className="w-4 h-4 mr-1" /> Incidents</TabsTrigger>
          <TabsTrigger value="toolbox" data-testid="cp-tab-toolbox"><Megaphone className="w-4 h-4 mr-1" /> Toolbox talks</TabsTrigger>
          <TabsTrigger value="wellbeing" data-testid="cp-tab-wellbeing"><HeartPulse className="w-4 h-4 mr-1" /> Wellbeing pulse</TabsTrigger>
          <TabsTrigger value="frameworks" data-testid="cp-tab-frameworks"><ShieldCheck className="w-4 h-4 mr-1" /> Framework builder</TabsTrigger>
          <TabsTrigger value="cert-queue" data-testid="cp-tab-cert-queue"><BookOpen className="w-4 h-4 mr-1" /> Cert queue</TabsTrigger>
          <TabsTrigger value="audit" data-testid="cp-tab-audit"><ScrollText className="w-4 h-4 mr-1" /> Audit log</TabsTrigger>
        </TabsList>
        <TabsContent value="incidents" className="mt-6"><IncidentsTab /></TabsContent>
        <TabsContent value="toolbox" className="mt-6"><ToolboxTab /></TabsContent>
        <TabsContent value="wellbeing" className="mt-6"><WellbeingTab /></TabsContent>
        <TabsContent value="frameworks" className="mt-6"><FrameworksTab /></TabsContent>
        <TabsContent value="cert-queue" className="mt-6"><CertQueueTab /></TabsContent>
        <TabsContent value="audit" className="mt-6"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function IncidentsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/compliance-plus/incidents").then(r=>setItems(r.data)).catch(()=>setItems([]));
  useEffect(() => { load(); }, []);
  const close = async (id) => {
    await api.patch(`/compliance-plus/incidents/${id}/close`, { resolution: "Investigated and resolved" });
    toast.success("Incident closed"); load();
  };
  return (
    <div className="space-y-3" data-testid="incidents-admin">
      {items.length === 0 && <div className="text-sm text-neutral-500">No incidents reported. Employees can report from /me/compliance.</div>}
      {items.map(i => (
        <Card key={i.inc_id} className="border-0 shadow-sm" data-testid={`incident-${i.inc_id}`}>
          <CardContent className="p-4 flex items-start justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={
                  i.severity === "high" ? "bg-red-50 text-red-500 border-red-200" :
                  i.severity === "medium" ? "bg-pink/10 text-pink border-pink/20" :
                  "bg-neutral-100 text-neutral-500"
                }>{i.severity} severity</Badge>
                <Badge variant="outline" className="text-[9px]">{i.kind}</Badge>
                <span className="text-xs text-neutral-500">{new Date(i.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm mt-2">{i.description}</div>
              <div className="text-xs text-neutral-500 mt-1">Reported by {i.reporter_name} · {i.location || "no location"}</div>
            </div>
            {i.status === "OPEN" && <Button size="sm" onClick={()=>close(i.inc_id)} data-testid={`incident-close-${i.inc_id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Close</Button>}
            {i.status === "CLOSED" && <Badge variant="outline" className="bg-teal/10 text-teal border-teal/20">Closed</Badge>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ToolboxTab() {
  const [talks, setTalks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title:"", content:"", duration_min:5 });
  const load = () => api.get("/compliance-plus/toolbox-talks").then(r=>setTalks(r.data)).catch(()=>setTalks([]));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) return toast.error("Fill in title and content");
    await api.post("/compliance-plus/toolbox-talks", form);
    setOpen(false); setForm({title:"", content:"", duration_min:5}); load(); toast.success("Talk added");
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between"><p className="text-sm text-neutral-500">{talks.length} talks · Employees acknowledge from /me/compliance</p><Button size="sm" onClick={()=>setOpen(true)} className="btn-primary rounded-full" data-testid="toolbox-new-btn"><Plus className="w-4 h-4 mr-1" /> New talk</Button></div>
      <div className="grid md:grid-cols-2 gap-4">
        {talks.map(t => (
          <Card key={t.talk_id} className="border-0 shadow-sm" data-testid={`talk-${t.talk_id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <h3 className="font-semibold">{t.title}</h3>
                <Badge variant="outline" className="text-[9px]">{t.duration_min} min</Badge>
              </div>
              <p className="text-sm text-neutral-600 mt-2">{t.content}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New toolbox talk</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} data-testid="toolbox-title" />
            <Textarea rows={5} placeholder="Content — 3-5 minute safety topic" value={form.content} onChange={e=>setForm({...form, content:e.target.value})} data-testid="toolbox-content" />
            <Input type="number" value={form.duration_min} onChange={e=>setForm({...form, duration_min:+e.target.value})} placeholder="Duration (minutes)" />
          </div>
          <DialogFooter><Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save} className="btn-primary" data-testid="toolbox-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WellbeingTab() {
  const [data, setData] = useState([]);
  useEffect(() => { api.get("/compliance-plus/wellbeing-pulse/summary").then(r=>setData(r.data)).catch(()=>setData([])); }, []);
  return (
    <div className="space-y-4" data-testid="wellbeing-summary">
      <p className="text-sm text-neutral-500">Anonymised weekly averages · past 90 days</p>
      {data.length === 0 && <div className="text-sm text-neutral-500 py-8">No pulse responses yet.</div>}
      <div className="space-y-3">
        {data.map(d => (
          <div key={d.week_start} data-testid={`wb-week-${d.week_start}`}>
            <div className="flex justify-between text-sm mb-1"><span>Week of {d.week_start}</span><span className="font-mono text-neutral-500">{d.avg_score}/10 · {d.responses} response(s)</span></div>
            <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
              <div className="h-full bg-pink" style={{ width: `${d.avg_score*10}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FrameworksTab() {
  const [monitors, setMonitors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name:"", framework:"SOC 2", kind:"manual", description:"" });
  const [searchParams, setSearchParams] = useSearchParams();
  const load = () => api.get("/compliance/monitors").then(r=>setMonitors(r.data)).catch(()=>setMonitors([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (searchParams.get("action") === "new-monitor") {
      setOpen(true);
      const p = new URLSearchParams(searchParams); p.delete("action"); setSearchParams(p, { replace: true });
    }
  }, [searchParams]);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    await api.post("/compliance-plus/monitors", form);
    setOpen(false); setForm({name:"", framework:"SOC 2", kind:"manual", description:""}); load(); toast.success("Monitor added");
  };
  const del = async (id) => {
    if (!window.confirm("Delete this monitor?")) return;
    try { await api.delete(`/compliance-plus/monitors/${id}`); load(); }
    catch(e){ toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const groups = monitors.reduce((acc, m) => { (acc[m.framework || "Other"] ||= []).push(m); return acc; }, {});
  return (
    <div className="space-y-4">
      <div className="flex justify-between"><p className="text-sm text-neutral-500">{monitors.length} monitors across {Object.keys(groups).length} frameworks</p><Button size="sm" onClick={()=>setOpen(true)} className="btn-primary rounded-full" data-testid="framework-new-btn"><Plus className="w-4 h-4 mr-1" /> New monitor</Button></div>
      {Object.entries(groups).map(([framework, list]) => (
        <Card key={framework} className="border-0 shadow-sm">
          <CardContent className="p-5">
            <h3 className="font-semibold mb-3">{framework} <span className="text-neutral-400 font-normal text-sm">· {list.length}</span></h3>
            <div className="space-y-1.5">
              {list.map(m => (
                <div key={m.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-neutral-50" data-testid={`monitor-${m.id}`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`w-2 h-2 rounded-full ${m.status==="PASSING"?"bg-teal":m.status==="WARNING"?"bg-amber-400":"bg-red-500"}`} />
                    <span className="text-sm truncate">{m.name}</span>
                  </div>
                  <Badge variant="outline" className="text-[9px] mr-2">{m.status}</Badge>
                  {m.is_custom && <button onClick={()=>del(m.id)} className="text-neutral-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add custom monitor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Monitor name — e.g. 'Encryption at rest verified'" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="framework-name" />
            <div>
              <Label className="text-xs">Framework</Label>
              <select className="w-full px-3 py-2 text-sm rounded-lg border border-neutral-200" value={form.framework} onChange={e=>setForm({...form, framework:e.target.value})} data-testid="framework-framework">
                {["SOC 2","GDPR","HIPAA","PCI DSS","ISO 27001","ISO 42001","ISO 45001","Custom"].map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <Textarea rows={2} placeholder="What does this monitor check?" value={form.description} onChange={e=>setForm({...form, description:e.target.value})} />
          </div>
          <DialogFooter><Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save} className="btn-primary" data-testid="framework-save">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CertQueueTab() {
  const [queue, setQueue] = useState([]);
  const load = () => api.get("/compliance-plus/cert-verification-queue").then(r=>setQueue(r.data)).catch(()=>setQueue([]));
  useEffect(() => { load(); }, []);
  const decide = async (cert_id, action) => {
    await api.patch(`/compliance-plus/cert-verification/${cert_id}`, { action });
    toast.success(`Certificate ${action.toLowerCase()}d`); load();
  };
  return (
    <div className="space-y-3" data-testid="cert-queue">
      {queue.length === 0 && <div className="text-sm text-neutral-500">Cert queue is clear — nothing to verify.</div>}
      {queue.map(c => (
        <Card key={c.cert_id} className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-semibold text-sm">{c.title || c.name || "Untitled certification"}</div>
              <div className="text-xs text-neutral-500 mt-1">{c.employee_name || "—"} · Expires {c.expiry_date || "n/a"} · Uploaded {new Date(c.uploaded_at || c.created_at || Date.now()).toLocaleDateString()}</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={()=>decide(c.cert_id, "APPROVE")} className="bg-teal hover:bg-teal/90 text-white" data-testid={`cert-approve-${c.cert_id}`}>Approve</Button>
              <Button size="sm" variant="outline" onClick={()=>decide(c.cert_id, "REJECT")} className="text-red-500 border-red-200" data-testid={`cert-reject-${c.cert_id}`}>Reject</Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AuditTab() {
  const [log, setLog] = useState([]);
  useEffect(() => { api.get("/compliance-plus/audit-log?limit=100").then(r=>setLog(r.data)).catch(()=>setLog([])); }, []);
  return (
    <div className="space-y-1" data-testid="audit-log">
      {log.length === 0 && <div className="text-sm text-neutral-500">No audit entries yet.</div>}
      {log.map(l => (
        <div key={l.log_id} className="flex items-center gap-3 py-2 px-3 rounded hover:bg-neutral-50 text-sm" data-testid={`audit-row-${l.log_id}`}>
          <div className="text-xs text-neutral-400 font-mono w-40 shrink-0">{new Date(l.at).toLocaleString([], {dateStyle:"short", timeStyle:"short"})}</div>
          <Badge variant="outline" className="text-[9px] shrink-0">{l.action}</Badge>
          <span className="text-neutral-600">{l.entity}</span>
          {l.entity_id && <span className="text-neutral-400 font-mono text-xs truncate">{l.entity_id}</span>}
          {l.meta?.name && <span className="text-neutral-500 truncate">· {l.meta.name}</span>}
        </div>
      ))}
    </div>
  );
}
